import { lazy, type ComponentType } from 'react';

type LazyComponent<TExport> =
  TExport extends ComponentType<infer Props> ? ComponentType<Props> : ComponentType;

function lazyNamed<TModule, TKey extends keyof TModule>(loader: () => Promise<TModule>, key: TKey) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[key] as unknown as LazyComponent<TModule[TKey]> };
  });
}

export const LoginPage = lazyNamed(() => import('../pages/LoginPage'), 'LoginPage');
export const ForgotPasswordPage = lazyNamed(
  () => import('../pages/ForgotPasswordPage'),
  'ForgotPasswordPage',
);
export const ResetPasswordPage = lazyNamed(
  () => import('../pages/ResetPasswordPage'),
  'ResetPasswordPage',
);
export const VerifyEmailPage = lazyNamed(
  () => import('../pages/VerifyEmailPage'),
  'VerifyEmailPage',
);
export const AcceptInvitationPage = lazyNamed(
  () => import('../pages/AcceptInvitationPage'),
  'AcceptInvitationPage',
);
export const ConnectPage = lazyNamed(() => import('../pages/ConnectPage'), 'ConnectPage');
export const AcceptAgencyInvitationPage = lazyNamed(
  () => import('../pages/AcceptAgencyInvitationPage'),
  'default',
);
export const ConfirmEmailChangePage = lazyNamed(
  () => import('../pages/ConfirmEmailChangePage'),
  'ConfirmEmailChangePage',
);
export const SelectStorePage = lazyNamed(
  () => import('../pages/SelectStorePage'),
  'SelectStorePage',
);
export const AccountPage = lazyNamed(() => import('../pages/AccountPage'), 'AccountPage');
export const ImportLegacyPage = lazyNamed(
  () => import('../pages/ImportLegacyPage'),
  'ImportLegacyPage',
);
export const OnboardingPage = lazyNamed(() => import('../pages/OnboardingPage'), 'OnboardingPage');
export const MaintenancePage = lazyNamed(
  () => import('../pages/MaintenancePage'),
  'MaintenancePage',
);

export const OverviewPage = lazyNamed(() => import('../pages/OverviewPage'), 'OverviewPage');
export const IntegrationsPage = lazyNamed(
  () => import('../pages/IntegrationsPage'),
  'IntegrationsPage',
);
export const OrdersPage = lazyNamed(() => import('../pages/OrdersPage'), 'OrdersPage');
export const CampaignsHubPage = lazyNamed(
  () => import('../pages/CampaignsHubPage'),
  'CampaignsHubPage',
);
export const HaloEffectPage = lazyNamed(() => import('../pages/HaloEffectPage'), 'HaloEffectPage');
export const HaloEffectV2Page = lazyNamed(
  () => import('../pages/HaloEffectV2Page'),
  'HaloEffectV2Page',
);
export const CalendarPage = lazyNamed(() => import('../pages/CalendarPage'), 'CalendarPage');
export const ReportsPage = lazyNamed(() => import('../pages/ReportsPage'), 'ReportsPage');
export const ReportBuilderPage = lazyNamed(
  () => import('../pages/ReportBuilderPage'),
  'ReportBuilderPage',
);
export const AskHaloPage = lazyNamed(() => import('../pages/AskHaloPage'), 'AskHaloPage');
export const SettingsPage = lazyNamed(() => import('../pages/SettingsPage'), 'SettingsPage');
export const DebugDataPage = lazyNamed(() => import('../pages/DebugDataPage'), 'DebugDataPage');

export const AgencyLayout = lazyNamed(() => import('../pages/agency/AgencyLayout'), 'default');
export const AgencyOverviewPage = lazyNamed(
  () => import('../pages/agency/AgencyOverviewPage'),
  'default',
);
export const AgencyPodsPage = lazyNamed(() => import('../pages/agency/AgencyPodsPage'), 'default');
export const AgencyStoresPage = lazyNamed(
  () => import('../pages/agency/AgencyStoresPage'),
  'default',
);
export const AgencyTeamPage = lazyNamed(() => import('../pages/agency/AgencyTeamPage'), 'default');
export const AgencyReportsPage = lazyNamed(
  () => import('../pages/agency/AgencyReportsPage'),
  'default',
);
export const AgencyNotificationsPage = lazyNamed(
  () => import('../pages/agency/AgencyNotificationsPage'),
  'default',
);
export const AgencyApiKeysPage = lazyNamed(
  () => import('../pages/agency/AgencyApiKeysPage'),
  'default',
);
export const AgencyAuditLogPage = lazyNamed(
  () => import('../pages/agency/AgencyAuditLogPage'),
  'default',
);
export const AgencySettingsPage = lazyNamed(
  () => import('../pages/agency/AgencySettingsPage'),
  'default',
);

export const PodLayout = lazyNamed(() => import('../pages/pod/PodLayout'), 'default');
export const PodOverviewPage = lazyNamed(() => import('../pages/pod/PodOverviewPage'), 'default');
export const PodStoresPage = lazyNamed(() => import('../pages/pod/PodStoresPage'), 'default');
export const PodTeamPage = lazyNamed(() => import('../pages/pod/PodTeamPage'), 'default');
export const PodApiKeysPage = lazyNamed(() => import('../pages/pod/PodApiKeysPage'), 'default');
export const PodAuditLogPage = lazyNamed(() => import('../pages/pod/PodAuditLogPage'), 'default');
export const PodSettingsPage = lazyNamed(() => import('../pages/pod/PodSettingsPage'), 'default');
